import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { HeroSection } from '../components/marketplace/HeroSection';
import { CategoryChips } from '../components/marketplace/CategoryChips';
import { RestaurantCard } from '../components/marketplace/RestaurantCard';
import { useMarketplaceData } from '../lib/hooks';
import { supabase } from '../lib/supabase';
import { EmptyState } from '../components/ui/EmptyState';
import { Store } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { isRestaurantOpenBySchedule } from '../lib/schedule';
// ─── Search helpers ──────────────────────────────────────────────────
//
// We want typing "hambur", "burger" or "hamburguesas" to find every
// restaurant that sells burgers — regardless of what the restaurant
// itself is called or how each restaurant spells the dish in its menu.
//
// Strategy:
//   1. normalizeText() strips accents and lowercases so "café" matches
//      "cafe" and "Hamburguesas" matches "hamburguesa".
//   2. expandQueryTerms() returns the original query PLUS any known
//      synonyms. Both directions are mapped (es↔en), and partial roots
//      like "hambur" expand to "burger".
const SEARCH_SYNONYMS: Record<string, string[]> = {
  hamburguesa: ['burger', 'hamburger'],
  hamburguesas: ['burger', 'burgers', 'hamburger'],
  hambur: ['burger', 'hamburger'],
  burger: ['hamburguesa', 'hamburguesas'],
  burgers: ['hamburguesa', 'hamburguesas'],
  pollo: ['chicken'],
  chicken: ['pollo'],
  helado: ['ice cream', 'icecream', 'sundae', 'nieve'],
  helados: ['ice cream', 'icecream', 'sundae'],
  cafe: ['coffee', 'café'],
  cafeteria: ['coffee', 'cafe'],
  cafetería: ['coffee', 'cafe'],
  coffee: ['cafe', 'café', 'cafeteria'],
  bebida: ['drink', 'bebidas'],
  bebidas: ['drink', 'drinks'],
  drink: ['bebida', 'bebidas'],
  parrilla: ['grill', 'parrillera', 'asado'],
  asado: ['parrilla', 'grill'],
  grill: ['parrilla', 'parrillera'],
  postre: ['dessert', 'postres', 'dulce'],
  postres: ['dessert', 'desserts'],
  pizza: ['pizzas'],
  sushi: ['roll', 'rolls', 'sashimi', 'nigiri'],
  roll: ['sushi'],
  rolls: ['sushi'],
  comida: ['food', 'plato', 'platos'],
  tipica: ['típica', 'casado', 'comida tipica'],
  típica: ['tipica', 'casado'],
  casado: ['típica', 'tipica', 'comida tipica'],
  desayuno: ['breakfast', 'desayunos'],
  desayunos: ['breakfast'],
  breakfast: ['desayuno', 'desayunos']
};
function normalizeText(s: string | null | undefined): string {
  if (!s) return '';
  return String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}
function expandQueryTerms(raw: string): string[] {
  const base = normalizeText(raw);
  if (!base) return [];
  const out = new Set<string>();
  out.add(base);
  // Also split the query into words and expand each, so a multi-word
  // query like "burger bueno" still expands "burger" → "hamburguesa".
  const words = base.split(/\s+/).filter(w => w.length >= 2);
  for (const w of words) out.add(w);
  for (const term of Array.from(out)) {
    const syns = SEARCH_SYNONYMS[term];
    if (syns) syns.forEach(s => out.add(normalizeText(s)));
    // Loose prefix expansion: if any synonym key starts with the term,
    // include all of that key's synonyms. Handles "hambur" → "hamburguesa"
    // → "burger".
    if (term.length >= 4) {
      for (const key of Object.keys(SEARCH_SYNONYMS)) {
        if (key.startsWith(term) || term.startsWith(key)) {
          out.add(key);
          SEARCH_SYNONYMS[key].forEach(s => out.add(normalizeText(s)));
        }
      }
    }
  }
  return Array.from(out).filter(t => t.length >= 2);
}
// Escape characters that have special meaning inside a PostgREST OR
// expression value. Commas and parentheses break the parser, so we drop
// them before injecting user input.
function escapeIlike(s: string): string {
  return s.replace(/[(),]/g, ' ');
}
export function HomePage() {
  const {
    categories,
    featuredRestaurants,
    allRestaurants,
    restaurantCategoryMap,
    zones,
    loading
  } = useMarketplaceData();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  // Restaurant IDs whose MENU contains an item matching the search query.
  // Populated asynchronously via debounced Supabase lookup. While loading,
  // we still apply the synchronous filters (name / description / category)
  // so the UI stays responsive.
  const [menuMatchRestaurantIds, setMenuMatchRestaurantIds] = useState<Set<string>>(new Set());
  const {
    t
  } = useLanguage();
  // Debounced search of menu_items + menu_categories → restaurant_ids whose
  // menu contains products or categories matching the query. This is what
  // makes "hambur" find every restaurant that sells hamburguesas, even if
  // the restaurant name is "La Cabaña Parrillera" and its products are
  // called "Burger Clásica".
  useEffect(() => {
    const raw = searchQuery.trim();
    if (raw.length < 2) {
      setMenuMatchRestaurantIds(new Set());
      return;
    }
    const terms = expandQueryTerms(raw);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        // Build OR conditions: name ilike for every expanded term, on both
        // menu_items.name and menu_items.description.
        const itemOr = terms.flatMap(t => [`name.ilike.%${escapeIlike(t)}%`, `description.ilike.%${escapeIlike(t)}%`]).join(',');
        const catOr = terms.map(t => `name.ilike.%${escapeIlike(t)}%`).join(',');
        const [itemsRes, catsRes] = await Promise.all([supabase.from('menu_items').select('restaurant_id').or(itemOr).eq('is_active', true)
        // Exclude POS internal products from marketplace search results.
        // Defensive null check in case the column hasn't been added yet
        // (the SETUP_POS_INTERNAL_PRODUCTS.sql migration adds it).
        .or('internal_only.is.null,internal_only.eq.false').or('product_type.is.null,product_type.neq.retail').or('show_in_digital_menu.is.null,show_in_digital_menu.eq.true').limit(1000), supabase.from('menu_categories').select('restaurant_id').or(catOr).limit(500)]);
        if (cancelled) return;
        if (itemsRes.error) {
          console.error('[NOIZA] menu items search error:', itemsRes.error);
        }
        if (catsRes.error) {
          console.error('[NOIZA] menu categories search error:', catsRes.error);
        }
        const ids = new Set<string>();
        for (const row of itemsRes.data || []) {
          if (row?.restaurant_id != null) ids.add(String(row.restaurant_id));
        }
        for (const row of catsRes.data || []) {
          if (row?.restaurant_id != null) ids.add(String(row.restaurant_id));
        }
        setMenuMatchRestaurantIds(ids);
      } catch (err) {
        if (!cancelled) console.error('[NOIZA] menu search exception:', err);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);
  const isThursdayUTC = new Date().getDay() === 4;
  const cabanaInList = allRestaurants.find(r => r.slug === 'la-cabana-parrillera' || String(r.id) === '24');
  // Filter restaurants by category and search
  const filterRestaurants = useMemo(() => {
    // Pre-compute a map of categoryId → category name (normalized) once per
    // category list change. Used so the search can match category names too
    // (e.g. typing "hambur" finds every restaurant tagged "Hamburguesas").
    const categoryNameById = new Map<string, string>();
    categories.forEach((c: any) => {
      if (c && c.id != null && c.name) {
        categoryNameById.set(String(c.id), normalizeText(c.name));
      }
    });
    return (restaurants: typeof allRestaurants) => {
      let filtered = restaurants;
      // Filter by category
      if (activeCategory) {
        filtered = filtered.filter(r => {
          const cats = restaurantCategoryMap[r.id];
          return cats && cats.includes(activeCategory);
        });
      }
      // Filter by search — match against name, descriptions, category names
      // the restaurant belongs to, AND menu items / menu categories found
      // via the async lookup. Search expands synonyms (hamburguesa↔burger,
      // etc.) and is accent-insensitive.
      if (searchQuery.trim()) {
        const terms = expandQueryTerms(searchQuery).map(normalizeText);
        filtered = filtered.filter(r => {
          const haystacks: string[] = [];
          if (r.name) haystacks.push(normalizeText(r.name));
          if ((r as any).description_short) {
            haystacks.push(normalizeText((r as any).description_short));
          }
          if ((r as any).description_long) {
            haystacks.push(normalizeText((r as any).description_long));
          }
          if (r.slug) haystacks.push(normalizeText(r.slug));
          const cats = restaurantCategoryMap[r.id] || [];
          for (const catId of cats) {
            const catName = categoryNameById.get(String(catId));
            if (catName) haystacks.push(catName);
          }
          for (const term of terms) {
            for (const hay of haystacks) {
              if (hay.includes(term)) return true;
            }
          }
          // Menu item / category match (async — set populated by the effect)
          if (menuMatchRestaurantIds.has(String(r.id))) return true;
          return false;
        });
      }
      return filtered;
    };
  }, [activeCategory, searchQuery, restaurantCategoryMap, categories, menuMatchRestaurantIds]);
  // Sort helper: open restaurants first, closed after
  const sortByOpenStatus = (restaurants: typeof allRestaurants) => {
    return [...restaurants].sort((a, b) => {
      const aStatus = isRestaurantOpenBySchedule(a.slug);
      const bStatus = isRestaurantOpenBySchedule(b.slug);
      const aOpen = aStatus.hasSchedule ? aStatus.isOpen : false;
      const bOpen = bStatus.hasSchedule ? bStatus.isOpen : false;
      if (aOpen === bOpen) return 0;
      return aOpen ? -1 : 1;
    });
  };
  const filteredFeatured = useMemo(() => sortByOpenStatus(filterRestaurants(featuredRestaurants)), [filterRestaurants, featuredRestaurants]);
  const filteredAll = useMemo(() => sortByOpenStatus(filterRestaurants(allRestaurants)), [filterRestaurants, allRestaurants]);
  const isFiltering = !!activeCategory || !!searchQuery.trim();
  return <div className="min-h-screen w-full flex flex-col bg-[#0B0B0B]" data-id="element-7520" data-mp="1aa6s0r16fznf">
      <MarketplaceHeader data-id="element-7521" data-mp="1a7ubmib2hxch" />

      <main className="flex-1" data-id="element-7522" data-mp="1f889j7iyfqws">
        <HeroSection searchQuery={searchQuery} onSearchChange={setSearchQuery} data-id="element-7523" data-mp="1f7gogqmzhbkr" />

        {/* Cabaña Parrillera — Promo Jueves banner DESACTIVADO por pedido
            del dueño. Ya no se muestra en el marketplace. */}

        <div className="max-w-7xl mx-auto py-8" data-id="element-7524" data-mp="3jicwd0qo8vxy">
          {/* Featured Section — hide when filtering yields 0 featured */}
          {filteredFeatured.length > 0 && <section className="px-4 sm:px-6 lg:px-8 mb-16" data-id="element-7525" data-mp="3ej08cco2dits">
              <div className="flex items-center justify-between mb-8" data-id="element-7526" data-mp="39l76fvtsnazb">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-7527" data-mp="34tp3x3c4h2c8">
                  <span className="w-1.5 h-7 bg-[#C9A34E] rounded-full" data-id="element-7528" data-mp="30rv93bolysjz" />
                  {t('featured')} {t('restaurants')}
                </h2>
              </div>

              {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7529" data-mp="2qto4ztff44ze">
                  {[1, 2, 3].map(i => <div key={i} className="h-[300px] rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-7530" data-mp="2lub8si2nplk5" />)}
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7531" data-mp="2h6uzcy05g2uu">
                  {filteredFeatured.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-7532" data-mp="277w2ecoqxvno" />)}
                </div>}
            </section>}

          {/* All Restaurants Section */}
          <section className="px-4 sm:px-6 lg:px-8 mb-16" data-id="element-7533" data-mp="2q6i49ts37qsn">
            <div className="flex items-center justify-between mb-8" data-id="element-7534" data-mp="2q4x51qutvjog">
              <div data-id="element-7535" data-mp="3dv9mbsbyqsrp">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-7536" data-mp="3dv1b04vhnvim">
                  <span className="w-1.5 h-7 bg-[#C9A34E] rounded-full" data-id="element-7537" data-mp="3iom0f8j0nh67" />
                  {isFiltering ? 'Resultados' : t('all_restaurants')}
                </h2>
                {!isFiltering && <p className="text-sm text-white/40 mt-1 pl-5" data-id="element-7538" data-mp="128fx6e3r8nac">
                    Cada restaurante gestiona sus pedidos directamente
                  </p>}
                {isFiltering && <p className="text-sm text-white/40 mt-1 pl-5" data-id="element-7539" data-mp="1bzkal9ds8032">
                    {filteredAll.length} restaurante
                    {filteredAll.length !== 1 ? 's' : ''} encontrado
                    {filteredAll.length !== 1 ? 's' : ''}
                  </p>}
              </div>
            </div>

            {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7540" data-mp="t7rhzg2mkgvv">
                {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-[300px] rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-7541" data-mp="29kzif54r5onk" />)}
              </div> : filteredAll.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7542" data-mp="e8p1kmhbg7t3">
                {filteredAll.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-7543" data-mp="n6r6hd467kln" />)}
              </div> : <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-7544" data-mp="12302m5srlhtu">
                <Store className="w-10 h-10 text-white/20 mx-auto mb-3" data-id="element-7545" data-mp="16wb21lv8ywbq" />
                <p className="text-white/60 font-medium" data-id="element-7546" data-mp="1awuysobwrxxc">
                  {isFiltering ? 'No se encontraron restaurantes con esos filtros' : 'No se encontraron restaurantes'}
                </p>
                <p className="text-white/30 text-sm mt-1" data-id="element-7547" data-mp="1kv2e72qju7x8">
                  {isFiltering ? 'Prueba con otra categoría o término de búsqueda.' : 'Aún no hay restaurantes disponibles. Vuelve pronto.'}
                </p>
                {isFiltering && <button onClick={() => {
              setActiveCategory('');
              setSearchQuery('');
            }} className="mt-4 px-6 py-2 rounded-full bg-[#C9A34E]/10 text-[#C9A34E] text-sm font-semibold border border-[#C9A34E]/30 hover:bg-[#C9A34E]/20 transition-colors" data-id="element-7548" data-mp="1l11wggal4c60">
                    Limpiar filtros
                  </button>}
              </div>}
          </section>
        </div>
      </main>

      <Footer data-id="element-7549" data-mp="1zvzqddldt9p7" />
    </div>;
}